
import React, { useState, useEffect, useCallback } from 'react';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { HelpCircle, Link, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import TagsInput from '../TagsInput';
import { EnhancedLabel, EnhancedTooltipTrigger } from '@/lib/shadcn-wrappers';
import NTBIntegration from '../../NTBIntegration';
import HashtagGenerationButton from '../../../production/HashtagGenerationButton';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';


const InterviewContentSection = ({ form }) => {
  const { toast } = useToast();
  const [externalTitle, setExternalTitle] = useState('');
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [titleFetchStatus, setTitleFetchStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Helper function to check if URL is NTB article
  const isNTBArticle = (url: string) => {
    return url && url.startsWith('ntb:');
  };

  // Helper to check if URL is valid external URL
  const isValidExternalUrl = (url: string) => {
    return url && !url.startsWith('ntb:') && (url.startsWith('http://') || url.startsWith('https://'));
  };

  // Extract a human-readable title from NTB context JSON
  const getNtbTitle = (ctx: string) => {
    try {
      const ntb = JSON.parse(ctx);
      return ntb?.title || 'NTB-artikkel';
    } catch {
      const first = (ctx || '').split('\n').find(l => l.trim()) || 'NTB-artikkel';
      return first.length <= 200 ? first : 'NTB-artikkel';
    }
  };

  // Watch context and contextUrl values for display
  const contextValue = form.watch('context');
  const contextUrlValue = form.watch('contextUrl');

  // Check if current article has NTB data
  const hasNTBData = isNTBArticle(contextUrlValue);
  const contextLooksLikeNTB = contextValue && contextValue.trim().startsWith('{') && 
    (contextValue.includes('"title"') || contextValue.includes('"content"'));

  // Parse existing context to get title if it exists
  useEffect(() => {
    if (contextValue) {
      try {
        const data = JSON.parse(contextValue);
        if (data.type === 'external_article' && data.title) {
          setExternalTitle(data.title);
          setTitleFetchStatus('success');
        }
      } catch {
        // Not JSON, ignore
      }
    }
  }, []);

  // Fetch title from URL
  const fetchTitle = useCallback(async (url: string) => {
    if (!isValidExternalUrl(url)) return;
    
    setIsFetchingTitle(true);
    setTitleFetchStatus('idle');
    
    try {
      const { data, error } = await supabase.functions.invoke('fetch-url-title', {
        body: { url }
      });
      
      if (error) throw error;
      
      if (data?.success && data?.title) {
        setExternalTitle(data.title);
        setTitleFetchStatus('success');
        // Store as JSON in context
        form.setValue('context', JSON.stringify({
          type: 'external_article',
          title: data.title,
          url: url
        }));
        toast({
          title: 'Tittel hentet',
          description: data.title,
        });
      } else {
        setTitleFetchStatus('error');
        toast({
          title: 'Kunne ikke hente tittel',
          description: 'Skriv inn tittelen manuelt',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching title:', error);
      setTitleFetchStatus('error');
    } finally {
      setIsFetchingTitle(false);
    }
  }, [form, toast]);

  // Handle title change (manual edit)
  const handleTitleChange = (newTitle: string) => {
    setExternalTitle(newTitle);
    if (newTitle && contextUrlValue) {
      form.setValue('context', JSON.stringify({
        type: 'external_article',
        title: newTitle,
        url: contextUrlValue
      }));
    }
  };

  return (
    <div className="border rounded-md p-4 pb-5">
      <h3 className="text-lg font-semibold mb-4">Intervjuinnhold</h3>
      
      {/* NTB Data Indicator */}
      {hasNTBData && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2">
          <div className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
            N
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">NTB-artikkel aktiv</p>
            <p className="text-xs text-blue-700 mt-1">
              Denne artikkelen bruker NTB-data: {getNtbTitle(contextValue)}
            </p>
          </div>
        </div>
      )}
      
      {/* Warning for potential NTB mismatch */}
      {!hasNTBData && contextLooksLikeNTB && (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2">
          <HelpCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Advarsel: NTB-data uten URL</p>
            <p className="text-xs text-amber-700 mt-1">
              Konteksten ser ut som NTB-data, men context_url mangler. Bruk NTB-integrasjonen under for å fikse dette.
            </p>
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        {/* NTB Integration */}
        <NTBIntegration
          onArticleSelect={(article) => {
            // Note: Don't set title here - NTB article title goes in context, not job title field
            form.setValue('context', article.context);
            form.setValue('contextUrl', article.contextUrl);
            if (article.contextImageUrl) {
              form.setValue('contextImageUrl', article.contextImageUrl);
            }
            if (article.contextImageCaption) {
              form.setValue('contextImageCaption', article.contextImageCaption);
            }
          }}
          className="mb-4"
        />


        {/* Manual context URL field for external links */}
        <FormField
          control={form.control}
          name="contextUrl"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <EnhancedLabel>Kontekst URL for ekstern artikkel (valgfri)</EnhancedLabel>
                <TooltipProvider>
                  <Tooltip>
                    <EnhancedTooltipTrigger asChild>
                      <span className="cursor-help">
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </span>
                    </EnhancedTooltipTrigger>
                    <TooltipContent className="max-w-80">
                      <p>Legg til en ekstern URL. Hvis du vil bruke NTB, bruk NTB-integrasjonen over.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <FormControl>
                  <div className="relative w-full">
                    <Input 
                      placeholder="https://example.com/article" 
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        const newUrl = e.target.value;
                        // Reset title state when URL changes
                        if (newUrl && isValidExternalUrl(newUrl)) {
                          setExternalTitle('');
                          setTitleFetchStatus('idle');
                        }
                      }}
                      onBlur={(e) => {
                        // Fetch title on blur if we have a valid URL and no title yet
                        const url = e.target.value;
                        if (isValidExternalUrl(url) && !externalTitle && !isFetchingTitle) {
                          fetchTitle(url);
                        }
                      }}
                      className="pl-8"
                    />
                    <Link className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  </div>
                </FormControl>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Hvis du legger til en URL, vil konteksten være klikkbar i artikkelen og åpne den eksterne siden.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* External article title field - shows when we have a valid external URL */}
        {isValidExternalUrl(contextUrlValue) && (
          <FormItem>
            <div className="flex items-center gap-2">
              <EnhancedLabel>Tittel på ekstern artikkel *</EnhancedLabel>
              {isFetchingTitle && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              {titleFetchStatus === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
              {titleFetchStatus === 'error' && <AlertCircle className="h-4 w-4 text-amber-500" />}
            </div>
            <FormControl>
              <Input
                value={externalTitle}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="F.eks. Yeast er en liten restaurant med store smaker"
              />
            </FormControl>
            <p className="text-xs text-muted-foreground mt-1">
              {titleFetchStatus === 'success' 
                ? 'Tittelen ble hentet automatisk. Du kan redigere den om nødvendig.'
                : titleFetchStatus === 'error'
                ? 'Kunne ikke hente tittel automatisk. Skriv inn tittelen manuelt.'
                : 'Tittelen hentes automatisk fra URL-en, eller du kan skrive den inn manuelt.'}
            </p>
          </FormItem>
        )}

        {/* Hidden context field to maintain form schema compatibility */}
        <FormField
          control={form.control}
          name="context"
          render={({ field }) => (
            <input type="hidden" {...field} />
          )}
        />
        
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Spørsmål</EnhancedLabel>
              <FormControl>
                <Textarea 
                  placeholder="Spørsmålet som ble stilt"
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <EnhancedLabel>Svar</EnhancedLabel>
              <FormControl>
                <Textarea 
                  placeholder="Svaret på spørsmålet"
                  className="min-h-[200px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between mb-2">
                <EnhancedLabel>Emneord (tags)</EnhancedLabel>
                <HashtagGenerationButton
                  question={form.watch('question') || ''}
                  answer={form.watch('answer') || ''}
                  onHashtagsGenerated={(hashtags) => {
                    console.log('[DEBUG] Generated hashtags received:', hashtags);
                    field.onChange(hashtags);
                  }}
                />
              </div>
              <FormControl>
                <TagsInput 
                  value={field.value} 
                  onChange={(newTags) => {
                    console.log('[DEBUG] TagsInput onChange called with:', newTags);
                    field.onChange(newTags);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Sponsored content checkbox */}
        <div className="border rounded-md p-4 mt-4">
          <h4 className="text-md font-semibold mb-3">Annonsørinnhold</h4>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isSponsored"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </FormControl>
                  <EnhancedLabel className="!mt-0 cursor-pointer">Annonsørinnhold (Betalt Brief)</EnhancedLabel>
                </FormItem>
              )}
            />
            {form.watch('isSponsored') && (
              <FormField
                control={form.control}
                name="sponsorCompany"
                render={({ field }) => (
                  <FormItem>
                    <EnhancedLabel>Annonsørens firmanavn</EnhancedLabel>
                    <FormControl>
                      <Input
                        placeholder="F.eks. Xpeng, DNB, Equinor..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewContentSection;
